// campaign-setup-edit.ts — every DECISION the campaign page's Audience and When
// tabs make, kept out of the components so the node-env jest suite can test it.
//
// The campaign page (SequenceDetail, /campaigns/:id) is both where a campaign is
// viewed and where it is edited. Messages were always editable there; the
// audience and the send time lived in a separate "Edit scheduled campaign" popup
// that only a standalone campaign could reach. They are tabs on the page now, for
// a sequence and a standalone campaign alike, with ONE lock rule:
//
//   the audience and the start time can change until the first message is sent.
//
// After that the dispatcher has frozen the contact list and every per-contact
// send time (backend `initCampaignInRedis`), so a change would be silently inert.
// A message that has not been sent stays editable on the Messages tab, and so
// does the wait before it — those rules are unchanged.
//
// HOW EACH KIND SAVES
//   • standalone campaign → `campaign-edit-pending`: a compare-and-set on the
//     campaign still being `pending`. If sending started while the user was
//     editing, the server refuses the whole edit and says so.
//   • sequence (drip) → patch the drip's own `boardIDArray` / `schedule`. The
//     backend drips hook (`syncPendingStepCampaign`) copies both onto the pending
//     step campaign under an atomic `pending` guard — the same path the wizard's
//     post-launch edit uses (FE#332). `campaign-edit-pending` refuses a drip step
//     outright, so it cannot be used here.

import type { Drip, DripStatus } from '@bt-common/schemas/bt/drip';
import type { PendingCampaignEditRequest } from '@common-models/campaign-edit';

import { sameAudienceIds } from './audience-selection';

export type CampaignDetailTab = 'messages' | 'audience' | 'when';

export const CAMPAIGN_DETAIL_TABS: readonly CampaignDetailTab[] = ['messages', 'audience', 'when'];

/** `?tab=` → a tab, or `undefined` for anything else (a stale or hand-typed
 *  link must land on the default tab, never on a blank page). */
export const parseCampaignDetailTab = (raw: string | null | undefined): CampaignDetailTab | undefined =>
    CAMPAIGN_DETAIL_TABS.find((tab) => tab === raw);

/** Why the audience and the start time can no longer change — or `null` when
 *  they still can. */
export type SetupLock = 'sending-started' | 'finished' | null;

const FINISHED_CAMPAIGN_STATUSES = new Set([
    'completed',
    'complete-sent',
    'complete-delivered',
    'complete-with-skips',
    'aborted',
    'expired',
]);

export function campaignSetupLock(input: {
    /** Any step has left `scheduled` (see sequence-detail `stepStateFromCampaign`). */
    hasDispatched: boolean;
    isStandalone: boolean;
    dripStatus?: DripStatus;
    standaloneStatus?: string;
}): SetupLock {
    if (input.hasDispatched) return 'sending-started';
    if (input.isStandalone) {
        // `campaign-edit-pending` only ever writes a `pending` campaign; offering
        // the form for any other status would only end in a refusal.
        if (input.standaloneStatus === 'pending') return null;
        return FINISHED_CAMPAIGN_STATUSES.has(String(input.standaloneStatus)) ? 'finished' : 'sending-started';
    }
    if (input.dripStatus === 'aborted' || input.dripStatus === 'completed') return 'finished';
    return null;
}

type T = (key: string, defaultValue: string) => string;

/** The sentence under the lock, per tab. */
export function setupLockText(lock: Exclude<SetupLock, null>, tab: 'audience' | 'when', t: T): string {
    if (lock === 'finished') {
        return tab === 'audience'
            ? t('campaignsRedesign.setup.audienceLockedFinished', 'This campaign has ended, so its audience can no longer change.')
            : t('campaignsRedesign.setup.whenLockedFinished', 'This campaign has ended, so its start time can no longer change.');
    }
    return tab === 'audience'
        ? t('campaignsRedesign.setup.audienceLocked', 'The audience is locked — the first message has already been sent.')
        : t('campaignsRedesign.setup.whenLocked', 'The start time is locked — sending has already started.');
}

// ── standalone: the `campaign-edit-pending` request, or null when unchanged ──

export function pendingAudienceRequest(
    campaignId: string,
    next: readonly string[],
    stored: readonly string[],
): PendingCampaignEditRequest | null {
    // An empty audience is refused server-side; the tab disables Save first.
    if (next.length === 0 || sameAudienceIds(next.map(String), stored.map(String))) return null;
    return { campaignId, boardIDArray: next.map(String) };
}

export function pendingSendAtRequest(
    campaignId: string,
    next: Date | undefined,
    stored: Date | undefined,
): PendingCampaignEditRequest | null {
    if (!next || next.getTime() === stored?.getTime()) return null;
    return { campaignId, dueDate: next.toISOString() };
}

// ── sequence: the drip patch ─────────────────────────────────────────────────

export const dripAudiencePatch = (drip: Drip, next: readonly string[]): Drip => ({
    ...drip,
    boardIDArray: next.map(String),
});

/**
 * Move a sequence's first send. `isTimeValid: true` is what makes the drip a
 * SCHEDULED one (see campaign-timing-schedule `hydrateTiming`). Recurrence is
 * carried over untouched: this tab only moves the time.
 */
export const dripSendAtPatch = (drip: Drip, next: Date): Drip => ({
    ...drip,
    schedule: { ...(drip.schedule ?? {}), dueDate: next.toISOString(), isTimeValid: true },
});

// ── the result of a Save ─────────────────────────────────────────────────────

export type SetupSaveOutcome =
    | { kind: 'saved' }
    /** The server refused: sending started while the user was editing. */
    | { kind: 'refused' }
    /** The call failed — nothing is known, so a retry is honest. */
    | { kind: 'error' };

export function setupSaveOutcomeText(
    outcome: SetupSaveOutcome,
    t: T,
): { tone: 'success' | 'warn' | 'error'; text: string } {
    if (outcome.kind === 'saved') {
        return { tone: 'success', text: t('campaignsRedesign.setup.saved', 'Saved.') };
    }
    if (outcome.kind === 'refused') {
        return {
            tone: 'warn',
            text: t('campaignsRedesign.setup.refused', 'Sending started before this was saved, so nothing changed.'),
        };
    }
    return { tone: 'error', text: t('campaignsRedesign.setup.error', 'Couldn’t save. Check your connection and try again.') };
}
